/// <amd-module name="Comments/Thread/Components/LoadMoreComponent" />
import { CommentExpandOptions, ICommentArtifactInfo, ICommentPage, ICommentsClient } from "Comments/Common/Contracts";
import * as Resources from "Comments/Thread/Resources";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { IUICommentLoadMore } from "./CommentThreadInternalTypes";

/// <amd-module name="Comments/Thread/Components/LoadMoreComponent" />
export interface ILoadMoreComponentProps {
    /**
     * Artifact info for loading more comments
     */
    artifactInfo: ICommentArtifactInfo;

    /**
     * Client for retrieving comments
     */
    commentsClient: ICommentsClient;

    /**
     * Number of comments currently loaded
     */
    count: number;

    /**
     * Total number of comments that may be loaded
     */
    totalCount: number;

    /**
     * Indicates whether or not threading is supported
     */
    supportsThreads: boolean;

    /**
     * The load more item as shown in the comment list
     */
    item: IUICommentLoadMore;

    /**
     * The continuation token to use when paging more comments
     */
    continuationToken?: string;

    /**
     * Handler for when new comments need to be added
     */
    updatePage(page: ICommentPage, item: IUICommentLoadMore): void;

    /**
     * Handler for errors that come out of retrieving more items.
     */
    handleError(item: IUICommentLoadMore, error: any): void;
}

interface ILoadMoreComponentState {
    isLoading: boolean;
}

/**
 * Component to handle the 'load more' link/button (internal use only)
 */
export class LoadMoreComponent extends VssComponent<ILoadMoreComponentProps, ILoadMoreComponentState> {
    constructor(props: ILoadMoreComponentProps, context: IVssComponentContext) {
        super(props, context);

        this.state = { isLoading: false };
    }

    public render(): JSX.Element {
        const { count, totalCount, item } = this.props;
        const { isLoading } = this.state;

        if (totalCount === count) {
            return <></>;
        }

        const loadMore: string = isLoading ? Resources.Loading : format(Resources.LoadMore, totalCount - count);
        const isChildThread: boolean = !!item.parentId;

        return (
            <>
                {!isChildThread && (
                    <Button text={loadMore} className="load-more-comments-button" onClick={this._loadMore} disabled={isLoading} />
                )}
                {isChildThread && (
                    <div className="load-more-comments-link">
                        {!isLoading && <Link onClick={this._loadMore}>{loadMore}</Link>}
                        {isLoading && (
                            <span className="flex-row">
                                <Spinner className="comment-thread-status-spinner" size={SpinnerSize.small} />
                                <span>{Resources.Loading}</span>
                            </span>
                        )}
                    </div>
                )}
            </>
        );
    }

    private _loadMore = async () => {
        const { artifactInfo, commentsClient, item, continuationToken, updatePage, handleError, supportsThreads } = this.props;
        const { isLoading } = this.state;

        if (isLoading) {
            return;
        }

        const includeChildrenFlag: number = supportsThreads && !item.parentId ? CommentExpandOptions.Children : 0;
        const flags: number = CommentExpandOptions.RenderedText + includeChildrenFlag + CommentExpandOptions.Reactions;

        try {
            this.setState({ isLoading: true });

            const page: ICommentPage = await commentsClient.getComments(artifactInfo, 10, false, item.parentId, continuationToken, flags);
            this.setState({ isLoading: false });
            updatePage(page, item);
        } catch (e) {
            handleError(item, e);
            this.setState({ isLoading: false });
        }
    };
}
